import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";

// Mock alerts data
const mockAlerts = [
  { 
    id: 1, 
    title: "Anomaly Detected in Network Traffic", 
    severity: "critical", 
    status: "active",
    source: "Network Monitor",
    timestamp: "2 min ago",
    description: "Unusual spike in outbound traffic from server cluster A",
    affectedEntities: ["Server-A1", "Server-A2", "Gateway-1"]
  },
  { 
    id: 2, 
    title: "High Volume Transaction Pattern", 
    severity: "high", 
    status: "investigating",
    source: "Transaction Analyzer",
    timestamp: "15 min ago",
    description: "Multiple high-value transactions detected in short timeframe",
    affectedEntities: ["Account-X123", "Account-Y456"]
  },
  { 
    id: 3, 
    title: "New Entity Relationship Discovered", 
    severity: "medium", 
    status: "active",
    source: "Graph Analyzer",
    timestamp: "32 min ago",
    description: "Previously unknown connection between entities identified",
    affectedEntities: ["Person-JD", "Organization-TC"]
  },
  { 
    id: 4, 
    title: "Data Quality Issue", 
    severity: "low", 
    status: "resolved",
    source: "Data Validator",
    timestamp: "1 hour ago",
    description: "Missing required fields in imported records",
    affectedEntities: ["Dataset-Q4"]
  },
  { 
    id: 5, 
    title: "Geographic Pattern Alert", 
    severity: "high", 
    status: "active",
    source: "GIS Monitor",
    timestamp: "45 min ago",
    description: "Clustered activity detected in monitored region",
    affectedEntities: ["Location-NYC", "Location-LA"]
  },
  { 
    id: 6, 
    title: "Scheduled Report Failure", 
    severity: "medium", 
    status: "resolved",
    source: "Report Scheduler",
    timestamp: "2 hours ago",
    description: "Weekly analytics report failed to generate",
    affectedEntities: ["Report-Weekly-01"]
  },
];

const mockReports = [
  { id: 1, name: "Weekly Entity Analysis", type: "scheduled", lastRun: "Today, 06:00", status: "completed", records: 12450 },
  { id: 2, name: "Network Monitoring Summary", type: "scheduled", lastRun: "Today, 08:00", status: "completed", records: 3280 },
  { id: 3, name: "Risk Assessment Q2", type: "manual", lastRun: "Yesterday", status: "completed", records: 8920 },
  { id: 4, name: "Compliance Audit Trail", type: "scheduled", lastRun: "Running...", status: "running", records: null },
  { id: 5, name: "Geographic Distribution", type: "manual", lastRun: "2 days ago", status: "completed", records: 5670 },
];

const alertRules = [
  { id: 1, name: "High-Value Transaction", enabled: true, triggers: 145, condition: "Amount > $100K" },
  { id: 2, name: "Network Anomaly", enabled: true, triggers: 23, condition: "Traffic deviation > 3σ" },
  { id: 3, name: "New Entity Link", enabled: false, triggers: 892, condition: "New relationship detected" },
  { id: 4, name: "Geographic Cluster", enabled: true, triggers: 56, condition: "5+ events within 1km" },
  { id: 5, name: "Data Quality Check", enabled: true, triggers: 12, condition: "Missing required fields" },
];

const severityConfig = {
  critical: { color: "bg-destructive", text: "text-destructive", icon: "fa-circle-exclamation" },
  high: { color: "bg-warning", text: "text-warning", icon: "fa-triangle-exclamation" },
  medium: { color: "bg-primary", text: "text-primary", icon: "fa-circle-info" },
  low: { color: "bg-muted-foreground", text: "text-muted-foreground", icon: "fa-circle-minus" },
};

const statusConfig = {
  active: { color: "bg-destructive/20 text-destructive border-destructive/30" },
  investigating: { color: "bg-warning/20 text-warning border-warning/30" },
  resolved: { color: "bg-success/20 text-success border-success/30" },
};

export default function AlertsReports() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("alerts");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const filteredAlerts = mockAlerts.filter(alert => {
    if (filterSeverity !== "all" && alert.severity !== filterSeverity) return false;
    if (filterStatus !== "all" && alert.status !== filterStatus) return false;
    return true;
  });

  const alertStats = {
    critical: mockAlerts.filter(a => a.severity === "critical" && a.status !== "resolved").length,
    high: mockAlerts.filter(a => a.severity === "high" && a.status !== "resolved").length,
    total: mockAlerts.filter(a => a.status !== "resolved").length,
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] lg:h-[calc(100vh-4rem)]">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Stats Header */}
        <div className="p-3 lg:p-6 border-b border-border bg-card/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg lg:text-xl font-semibold">{t("monitoringCenter")}</h2>
              <p className="text-xs lg:text-sm text-muted-foreground hidden sm:block">{t("realTimeAlerts")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs lg:text-sm">
                <i className="fa-solid fa-download mr-1 lg:mr-2"></i>
                <span className="hidden sm:inline">{t("export")}</span>
              </Button>
              <Button size="sm" className="h-8 text-xs lg:text-sm">
                <i className="fa-solid fa-plus mr-1 lg:mr-2"></i>
                <span className="hidden sm:inline">{t("newAlertRule")}</span>
                <span className="sm:hidden">{t("new")}</span>
              </Button>
            </div>
          </div>

          {/* Quick Stats - Responsive Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
            <Card className="bg-destructive/10 border-destructive/30">
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-muted-foreground">{t("critical")}</p>
                    <p className="text-xl lg:text-2xl font-bold text-destructive">{alertStats.critical}</p>
                  </div>
                  <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-full bg-destructive/20">
                    <i className="fa-solid fa-circle-exclamation text-destructive text-sm lg:text-base"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-warning/10 border-warning/30">
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-muted-foreground">{t("highPriority")}</p>
                    <p className="text-xl lg:text-2xl font-bold text-warning">{alertStats.high}</p>
                  </div>
                  <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-full bg-warning/20">
                    <i className="fa-solid fa-triangle-exclamation text-warning text-sm lg:text-base"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-muted-foreground">{t("totalActive")}</p>
                    <p className="text-xl lg:text-2xl font-bold">{alertStats.total}</p>
                  </div>
                  <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-full bg-primary/20">
                    <i className="fa-solid fa-bell text-primary text-sm lg:text-base"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-muted-foreground">{t("reportsToday")}</p>
                    <p className="text-xl lg:text-2xl font-bold">{mockReports.filter(r => r.lastRun.includes("Today")).length}</p>
                  </div>
                  <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-full bg-chart-2/20">
                    <i className="fa-solid fa-file-lines text-chart-2 text-sm lg:text-base"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <div className="px-3 lg:px-6 pt-3 lg:pt-4 border-b border-border overflow-x-auto">
              <TabsList className="w-full lg:w-auto">
                <TabsTrigger value="alerts" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
                  <i className="fa-solid fa-bell text-xs"></i>
                  <span className="hidden sm:inline">{t("alerts")}</span>
                  <Badge variant="secondary" className="ml-1 text-[10px] lg:text-xs">{alertStats.total}</Badge>
                </TabsTrigger>
                <TabsTrigger value="reports" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
                  <i className="fa-solid fa-file-lines text-xs"></i>
                  <span className="hidden sm:inline">{t("reports")}</span>
                </TabsTrigger>
                <TabsTrigger value="rules" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
                  <i className="fa-solid fa-gear text-xs"></i>
                  <span className="hidden sm:inline">{t("alertRules")}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="alerts" className="flex-1 overflow-hidden m-0">
              <div className="flex flex-col h-full">
                {/* Alerts List */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Filters */}
                  <div className="p-3 lg:p-4 border-b border-border flex flex-wrap items-center gap-2 lg:gap-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="lg:hidden h-8 text-xs"
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                    >
                      <i className="fa-solid fa-filter mr-1"></i>
                      {t("filters")}
                    </Button>
                    <div className={cn("flex items-center gap-2", showMobileFilters ? "flex" : "hidden lg:flex")}>
                      <Label className="text-xs lg:text-sm">{t("severity")}:</Label>
                      <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                        <SelectTrigger className="w-24 lg:w-32 h-8 text-xs lg:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("all")}</SelectItem>
                          <SelectItem value="critical">{t("critical")}</SelectItem>
                          <SelectItem value="high">{t("high")}</SelectItem>
                          <SelectItem value="medium">{t("medium")}</SelectItem>
                          <SelectItem value="low">{t("low")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className={cn("flex items-center gap-2", showMobileFilters ? "flex" : "hidden lg:flex")}>
                      <Label className="text-xs lg:text-sm">{t("status")}:</Label>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-24 lg:w-32 h-8 text-xs lg:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("all")}</SelectItem>
                          <SelectItem value="active">{t("active")}</SelectItem>
                          <SelectItem value="investigating">{t("investigating")}</SelectItem>
                          <SelectItem value="resolved">{t("resolved")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1" />
                    <Input placeholder={t("searchAlerts")} className="w-full sm:w-48 lg:w-64 h-8 text-xs lg:text-sm" />
                  </div>

                  {/* Alerts Feed */}
                  <ScrollArea className="flex-1 p-3 lg:p-4">
                    <div className="space-y-2 lg:space-y-3">
                      {filteredAlerts.map((alert, index) => {
                        const severity = severityConfig[alert.severity];
                        const status = statusConfig[alert.status];
                        const isSelected = selectedAlert?.id === alert.id;

                        return (
                          <motion.div
                            key={alert.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card
                              className={cn(
                                "cursor-pointer transition-all",
                                isSelected ? "border-primary shadow-glow" : "hover:border-primary/50"
                              )}
                              onClick={() => setSelectedAlert(isSelected ? null : alert)}
                            >
                              <CardContent className="p-3 lg:p-4">
                                <div className="flex items-start gap-3 lg:gap-4">
                                  <div className={cn(
                                    "flex h-8 w-8 lg:h-10 lg:w-10 shrink-0 items-center justify-center rounded-full",
                                    severity.color + "/20"
                                  )}>
                                    <i className={cn("fa-solid text-xs lg:text-sm", severity.icon, severity.text)}></i>
                                  </div>

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <h4 className="font-medium text-foreground text-xs lg:text-sm truncate">{alert.title}</h4>
                                      <Badge variant="outline" className={cn("capitalize text-[10px] lg:text-xs", status.color)}>
                                        {t(alert.status) || alert.status}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-1 hidden sm:block">{alert.description}</p>
                                    <div className="flex items-center gap-3 lg:gap-4 mt-1 lg:mt-2 text-[10px] lg:text-xs text-muted-foreground">
                                      <span className="flex items-center gap-1 hidden sm:flex">
                                        <i className="fa-solid fa-satellite-dish"></i>
                                        {alert.source}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <i className="fa-solid fa-clock"></i>
                                        {alert.timestamp}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1 lg:gap-2">
                                    <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8">
                                      <i className="fa-solid fa-check text-xs"></i>
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8 hidden sm:flex">
                                      <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
                                    </Button>
                                  </div>
                                </div>

                                {isSelected && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    className="mt-3 lg:mt-4 pt-3 lg:pt-4 border-t border-border"
                                  >
                                    <div className="space-y-3">
                                      <div>
                                        <p className="text-[10px] lg:text-xs text-muted-foreground uppercase tracking-wide mb-2">{t("affectedEntities")}</p>
                                        <div className="flex flex-wrap gap-1 lg:gap-2">
                                          {alert.affectedEntities.map((entity, idx) => (
                                            <Badge key={idx} variant="outline" className="text-[10px] lg:text-xs">
                                              <i className="fa-solid fa-link mr-1 text-[8px] lg:text-[10px]"></i>
                                              {entity}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="flex gap-2 flex-wrap">
                                        <Button size="sm" variant="outline" className="h-7 lg:h-8 text-[10px] lg:text-xs">
                                          <i className="fa-solid fa-diagram-project mr-1 lg:mr-2"></i>
                                          {t("graph")}
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-7 lg:h-8 text-[10px] lg:text-xs">
                                          <i className="fa-solid fa-timeline mr-1 lg:mr-2"></i>
                                          {t("timeline")}
                                        </Button>
                                        <Button size="sm" className="h-7 lg:h-8 text-[10px] lg:text-xs">
                                          <i className="fa-solid fa-magnifying-glass mr-1 lg:mr-2"></i>
                                          {t("investigate")}
                                        </Button>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="reports" className="flex-1 overflow-hidden m-0 p-3 lg:p-6">
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs lg:text-sm">{t("reportName")}</TableHead>
                        <TableHead className="text-xs lg:text-sm hidden sm:table-cell">{t("type")}</TableHead>
                        <TableHead className="text-xs lg:text-sm hidden md:table-cell">{t("lastRun")}</TableHead>
                        <TableHead className="text-xs lg:text-sm">{t("status")}</TableHead>
                        <TableHead className="text-xs lg:text-sm hidden lg:table-cell">{t("records")}</TableHead>
                        <TableHead className="text-right text-xs lg:text-sm">{t("actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockReports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell>
                            <div className="flex items-center gap-2 lg:gap-3">
                              <div className="flex h-6 w-6 lg:h-8 lg:w-8 items-center justify-center rounded-lg bg-chart-2/10">
                                <i className="fa-solid fa-file-lines text-chart-2 text-xs lg:text-sm"></i>
                              </div>
                              <span className="font-medium text-xs lg:text-sm truncate max-w-[100px] lg:max-w-none">{report.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge variant="outline" className="capitalize text-[10px] lg:text-xs">{report.type}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[10px] lg:text-sm hidden md:table-cell">{report.lastRun}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={cn(
                                "text-[10px] lg:text-xs",
                                report.status === "completed" && "bg-success/20 text-success border-success/30",
                                report.status === "running" && "bg-primary/20 text-primary border-primary/30"
                              )}
                            >
                              {report.status === "running" && (
                                <i className="fa-solid fa-spinner fa-spin mr-1 text-[8px]"></i>
                              )}
                              {t(report.status) || report.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-xs">
                            {report.records ? report.records.toLocaleString() : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8">
                                <i className="fa-solid fa-download text-xs"></i>
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8 hidden sm:flex">
                                <i className="fa-solid fa-play text-xs"></i>
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8">
                                <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="rules" className="flex-1 overflow-hidden m-0 p-3 lg:p-6">
              <Card>
                <CardHeader className="p-3 lg:p-6">
                  <CardTitle className="text-sm lg:text-base">{t("alertRulesConfig")}</CardTitle>
                  <CardDescription className="text-xs lg:text-sm">{t("manageAutomatedAlerts")}</CardDescription>
                </CardHeader>
                <CardContent className="p-3 lg:p-6 pt-0">
                  <div className="space-y-3 lg:space-y-4">
                    {alertRules.map((rule) => (
                      <div 
                        key={rule.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 lg:p-4 rounded-lg border border-border gap-3"
                      >
                        <div className="flex items-center gap-3 lg:gap-4">
                          <Switch checked={rule.enabled} />
                          <div className="min-w-0">
                            <h4 className="font-medium text-xs lg:text-sm truncate">{rule.name}</h4>
                            <p className="text-[10px] lg:text-sm text-muted-foreground truncate">{rule.condition}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 lg:gap-4 ml-auto">
                          <div className="text-right">
                            <p className="text-xs lg:text-sm font-medium">{rule.triggers}</p>
                            <p className="text-[10px] lg:text-xs text-muted-foreground">{t("triggers")}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8">
                            <i className="fa-solid fa-pen text-xs"></i>
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8">
                            <i className="fa-solid fa-trash text-xs"></i>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
